import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MOBILE_WIDTH, TAB_LARGE_WIDTH } from "../../../utils/constants";
import { useScreenSize } from "../../../hooks";
import ChatToggleDesktop from "assets/mcat-chat/chat-toggle-dekstop.svg";
import ChatToggleMobile from "assets/mcat-chat/chat-toggle-mobile.svg";
import ChatToggleMobileDark from "assets/mcat-chat/chat-toggle-mobile-dark.svg";
import { Typography } from "components/atoms";
import SparkleIcon from "assets/mcat-chat/mcat-chat-icon.svg";
import SparkleIconMobileDark from "assets/mcat-chat/mcat-chat-icon-mobile-dark.svg";
import { AI_CHAT_MINIMIZE_OR_EXPAND_PANEL } from "utils/constants/GoogleTagGUIDs";
import "./index.scss";

interface IProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
  drawerClassName?: string;
  drawerRef?: any;
  theme: "light" | "dark";
}

const ChatSideDrawer: React.FC<IProps> = ({
  isOpen,
  onToggle,
  onClose,
  children,
  drawerClassName,
  drawerRef,
  theme,
}) => {
  const [screenSize, setScreenSize] = useState("desktop");
  const screen = useScreenSize();

  // Determine screen size for responsive behavior
  useEffect(() => {
    const updateSize = () => {
      if (screen.width < MOBILE_WIDTH) setScreenSize("mobile");
      else if (screen.width > MOBILE_WIDTH && screen.width <= TAB_LARGE_WIDTH)
        setScreenSize("tablet");
      else setScreenSize("desktop");
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [screen.width]);

  return createPortal(
    <>
      {/* Show backdrop only for tablet & mobile */}
      {isOpen && (screenSize === "tablet" || screenSize === "mobile") && (
        <div className="chat-overlay" />
      )}

      {/* Chat Sidebar */}
      <div className={
        `chat-sidebar 
        ${isOpen ? "open" : "collapsed"}
        ${screenSize}  
        theme-${theme} ${drawerClassName ?? ""}
        `
      }
        ref={drawerRef ?? null}
      >
        {
          !isOpen && <div className='drawer-invisible-overlay'></div>
        }
        {!isOpen && screenSize === "mobile" && (
          <div className="mobile-collapsed-content">
            <div className="mobile-collapsed-content-header">
              <div className="tutor-icon">
                <img src={theme === "dark" ? SparkleIconMobileDark : SparkleIcon} alt="AI Tutor Sparkle" />
              </div>
              <Typography variant="h6Bold" label="AI Tutor" />
            </div>
            <button
              className="chat-toggle mobile"
              onClick={onToggle}
              aria-label="Expand chat"
              data-tracking-id={AI_CHAT_MINIMIZE_OR_EXPAND_PANEL}
            >
              <img
                src={
                  theme === "light" ? ChatToggleMobile : ChatToggleMobileDark
                }
                alt="Expand chat"
                className="toggle-icon closed"
              />
            </button>
          </div>
        )}

        <div className="chat-content">{children}</div>

        {(isOpen || screenSize !== "mobile") && (
          <button
            className={`chat-toggle ${screenSize} ${
              isOpen ? "open" : "closed"
            }`}
            onClick={onToggle}
            aria-label={isOpen ? "Collapse chat" : "Expand chat"}
            data-tracking-id={AI_CHAT_MINIMIZE_OR_EXPAND_PANEL}
          >
            {screenSize === "mobile" ? (
              // Mobile toggle icons
              <img
                src={theme === "light" ? ChatToggleMobile : ChatToggleMobileDark}
                alt={isOpen ? "Collapse chat" : "Expand chat"}
                className={`toggle-icon ${isOpen ? "open" : "closed"}`}
              />
            ) : (
              // Desktop/tablet toggle icon
              <img
                src={ChatToggleDesktop}
                alt={isOpen ? "Collapse chat" : "Expand chat"}
                className={`toggle-icon ${isOpen ? "open" : "closed"}`}
              />
            )}
          </button>
        )}
      </div>
    </>,
    document.body
  );
};

export default ChatSideDrawer;
